import React, { useEffect, useRef, useState, useCallback } from "react";


import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import FeatureHeader from "../components/FeatureHeader";
import { Search, Bookmark, Clock, Filter, Mic, ShieldCheckIcon,  } from "lucide-react";
import { useNotifications } from "../contexts/NotificationContext";
import healthy from "../assets/icons/healthy.png"
import selfCare from "../assets/icons/self-care.png"
import longTermCare from "../assets/icons/long.png"
import mind from "../assets/icons/mind.png"
import pediatrics from "../assets/icons/pediatrics.png"
import pregnant from "../assets/icons/pregnant.png"
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import sexualHealth from "../assets/icons/sexual-health.png"
import abs from "../assets/icons/abs.png"
import placeholder from "../assets/icons/healthy.png"; // or any default image


// TrustStripe dependencies from QuickResponse.jsx
const C = {
  purple: "#6C5CE7",
  purpleLight: "#F0EEFF",
  purpleBg: "#EEE9FF",
  red: "#E74C3C",
  redLight: "#FFF0F0",
  green: "#27AE60",
  greenLight: "#E8F8EE",
  greenBg: "#F0FFF6",
  blue: "#3498DB",
  blueBg: "#E8F4FD",
  yellow: "#F39C12",
  yellowBg: "#FFF8E1",
  bg: "#F4F4F8",
  white: "#FFFFFF",
  text: "#111111",
  sub: "#888888",
  border: "#EEEEEE",
  dark: "#555555",
};

// Custom icons to match the image
const ExpertIcon = () => (
  <ShieldCheckIcon  width = {15} height={15} color={C.purple} />
);
const BookIcon = () => (
  <svg width="15" height="15" viewBox="0 0 22 22" fill="none">
    <rect x="3" y="5" width="16" height="12" rx="2.5" stroke={C.purple} strokeWidth="2" fill="none" />
    <path d="M11 5v12" stroke={C.purple} strokeWidth="2" />
  </svg>
);
const PersonHeartIcon = () => (
  <svg width="15" height="15" viewBox="0 0 22 22" fill="none">
    <circle cx="11" cy="7" r="4" stroke={C.purple} strokeWidth="2" fill="none" />
    <path d="M4 20c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke={C.purple} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M7.5 16.5c1.5 2 5.5 2 7 0" stroke={C.purple} strokeWidth="2" fill="none" />
    <path d="M11 18.5l1.5-1.5a2 2 0 00-3 0L11 18.5z" fill={C.purple} />
  </svg>
);

const trustHighlights = [
  {
    Icon: ExpertIcon,
    title: 'Expert',
    subtitle: 'Reviewed',
  },
  {
    Icon: BookIcon,
    title: 'Easy to',
    subtitle: 'Understand',
  },
  {
    Icon: PersonHeartIcon,
    title: 'Personalized',
    subtitle: 'for You',
  },
];

const stylesTrust = {
  trustStrip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
    marginBottom: 10,
    marginTop: 8,
    background: "#ffffff",
    border: "1px solid #eceef1",
    borderRadius: 999,
    padding: "6px 8px",
    overflowX: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  trustTrack: {
    display: "flex",
    alignItems: "center",
    width: "max-content",
    gap: 6,
  },
  trustItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 100,
  },
  trustItemIconWrap: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "1px solid #eceef1",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  trustItemCopy: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.15,
  },
  trustItemTitle: {
    fontSize: 11,
    color: "#3D3A52",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  trustItemSubtitle: {
    fontSize: 9,
    color: "#A0A0B2",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  trustStripDivider: {
    width: 1,
    height: 20,
    background: "#d6d2e7",
    flexShrink: 0,
  },
};

const SearchRow = ({
  query,
  setQuery,
  setPage,
  load,
  activeCategory,
  navigate,
}) => {
  const [isListening, setIsListening] =
    useState(false);

  const recognitionRef = useRef(null);

  const [searchValue, setSearchValue] =
    useState(query);

  // LIVE SEARCH
  useEffect(() => {
    const delay = setTimeout(() => {
      setQuery(searchValue);
      setPage(1);

      load(1, searchValue, activeCategory);
    }, 400);

    return () => clearTimeout(delay);
  }, [searchValue]);

  // VOICE SEARCH
  const handleMicClick = () => {
    const SpeechRecognition =
      window.SpeechRecognition ||
      window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert(
        "Speech recognition is not supported"
      );
      return;
    }

    if (
      isListening &&
      recognitionRef.current
    ) {
      recognitionRef.current.stop();
      return;
    }

    const recognition =
      new SpeechRecognition();

    recognition.lang = "en-US";
    recognition.interimResults = false;

    recognitionRef.current = recognition;

    recognition.onstart = () =>
      setIsListening(true);

    recognition.onend = () =>
      setIsListening(false);

    recognition.onresult = (e) => {
      const transcript =
        e.results[0][0].transcript;

      setSearchValue(transcript);
    };

    recognition.start();
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 6,
          height: 64,
          borderRadius: 32,
          padding: "6px 10px",
          background: "#f7f4ff",
          border: "1px solid #ece5ff",
          boxShadow:
            "0 2px 8px rgba(124,77,255,0.08)",
        }}
      >
        {/* AI BUTTON */}
        <div
          onClick={() => navigate("/asha")}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#efe8ff",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            cursor: "pointer",
          }}
        >
          <AutoAwesomeIcon
            style={{
              fontSize: 20,
              color: "var(--primary)",
            }}
          />
        </div>

        {/* SEARCH INPUT */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 8,
            position: "relative",
            background: "#fff",
            border: "1px solid #eee8ff",
            borderRadius: 26,
            padding: "0 46px 0 12px",
            height: 50,
          }}
        >
          <Search
            size={17}
            color="var(--primary)"
            style={{
              flexShrink: 0,
            }}
          />

          <input
            type="text"
            placeholder="Search health articles..."
            value={searchValue}
            onChange={(e) =>
              setSearchValue(e.target.value)
            }
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 13,
              color: "#333",
            }}
          />

          {/* MIC */}
          <div
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              height: 22,
              paddingLeft: 10,
              borderLeft:
                "1px solid #e6e6e8",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Mic
              size={19}
              color={
                isListening
                  ? "#ff3b30"
                  : "var(--primary)"
              }
              style={{
                cursor: "pointer",
              }}
              onClick={handleMicClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

function TrustStripe() {
  const trustStripRef = React.useRef(null);
  const [isTrustStripPaused, setIsTrustStripPaused] = React.useState(false);
  React.useEffect(() => {
    const strip = trustStripRef.current;
    if (!strip || isTrustStripPaused) return;
    const interval = setInterval(() => {
      const resetPoint = strip.scrollWidth / 2;
      if (resetPoint <= 0) return;
      if (strip.scrollLeft >= resetPoint) {
        strip.scrollLeft -= resetPoint;
      }
      strip.scrollBy({ left: 1, behavior: 'auto' });
    }, 24);
    return () => clearInterval(interval);
  }, [isTrustStripPaused]);
  return (
    <>
    {/*

    <div
      ref={trustStripRef}
      className="trust-strip-scroll"
      style={stylesTrust.trustStrip}
      onMouseEnter={() => setIsTrustStripPaused(true)}
      onMouseLeave={() => setIsTrustStripPaused(false)}
      onTouchStart={() => setIsTrustStripPaused(true)}
      onTouchEnd={() => setIsTrustStripPaused(false)}
    >
      <div style={stylesTrust.trustTrack}>
        {[...trustHighlights, ...trustHighlights].map((item, index, list) => {
          const Icon = item.Icon;
          return (
            <React.Fragment key={`${item.title}-${index}`}>
              <div style={stylesTrust.trustItem}>
                <div style={stylesTrust.trustItemIconWrap}>
                  <Icon size={5} color="var(--primary)" />
                </div>
                <div style={stylesTrust.trustItemCopy}>
                  <span style={stylesTrust.trustItemTitle}>{item.title}</span>
                  <span style={stylesTrust.trustItemSubtitle}>{item.subtitle}</span>
                </div>
              </div>
              {index < list.length - 1 && <div style={stylesTrust.trustStripDivider}></div>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
     */}
     </>
  );
}
export default function Blogs() {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const blogNotifiedRef = useRef(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [blogs, setBlogs] = useState([]);
  const [meta, setMeta] = useState({ totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [continueReading, setContinueReading] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All Articles");
  const [activeTab, setActiveTab] = useState("all");
  const [savedBlogs, setSavedBlogs] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  const categories = [
   "All Articles",
    "Everyday Health",
    "Women's Health",
     "Men's Health",
    "Chronic Conditions",
    "Mental Health",
    "Child and Family Health",
    "Sexual Health",
    "Nutrition and LifeStyle",
   
  ];


  useEffect(() => {
  const saved = JSON.parse(localStorage.getItem("readBlogs")) || [];
  setContinueReading(saved);
}, []);


  const load = async (p = page, q = query, cat = activeCategory) => {
    setLoading(true);
    try {
      const res = await api.get("/api/blogs", {
        params: { page: p, limit, q, category: cat !== "All Articles" ? cat : "" },
      });
      const data = res.data;
      const list = Array.isArray(data.blogs) ? data.blogs : Array.isArray(data) ? data : [];
      setBlogs(list);
      setMeta(data.meta || { totalPages: 1 });
    } catch (err) {
      console.error("Failed to load blogs", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, "", "All Articles");
  }, []);

  // Detect new blog articles and notify once per session
  useEffect(() => {
    if (blogs.length === 0 || blogNotifiedRef.current) return;
    const sessionKey = "qureo.blogsSessionNotified";
    if (sessionStorage.getItem(sessionKey)) return;
    const knownKey = "qureo.latestBlogId";
    const latestBlog = blogs[0];
    const latestId = latestBlog?._id;
    const knownId = localStorage.getItem(knownKey);
    if (latestId && knownId && knownId !== latestId) {
      addNotification({
        type: "new_blog",
        title: "New Health Article",
        body: `New post: "${latestBlog.title || "Health Article"}" — check it out in Learn Health`,
        icon: "📰",
      });
    }
    if (latestId) localStorage.setItem(knownKey, latestId);
    sessionStorage.setItem(sessionKey, "1");
    blogNotifiedRef.current = true;
  }, [blogs, addNotification]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setPage(1);
    await load(1, query);
  };

  const handleCategoryChange = (cat) => {
    setActiveTopCategory(cat);
    setPage(1);
    load(1, query, cat);
  };

  const loadSavedBlogs = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setSavedBlogs([]);
      return;
    }
    setLoadingSaved(true);
    try {
      const res = await api.get("/api/blogs/saved/user", { params: { userId } });
      setSavedBlogs(res.data.blogs || []);
    } catch (err) {
      console.error("Failed to load saved blogs", err);
      setSavedBlogs([]);
    } finally {
      setLoadingSaved(false);
    }
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    if (tab === "saved") {
      await loadSavedBlogs();
    }
  };

  const goto = (p) => {
    if (p < 1 || p > meta.totalPages) return;
    setPage(p);
    load(p, query, activeCategory);
  };

  const featured = blogs.slice(0, 4);
  const rest = blogs.slice(2);

// Instead of separate Featured + All Articles
const recommended = blogs.slice(0, 5); // adjust count as needed
const [activeTopCategory, setActiveTopCategory] = useState("All Articles");

  return (
  <div className="px-3 pb-5" style={{  minHeight: "100vh" }}>
    {/* Trust Stripe (scrollable, auto-scrolling) */}

  {/* Header */}

   <div style={styles.header}>
      <FeatureHeader
        title="Health Education"
        subtitle="Learn about your health, at your pace"
        onBack={() => navigate(-1)}
        containerStyle={{ padding: 0 }}
      />

    </div>
  

      <TrustStripe />
  {/* Search */}
  <div className="mb-3" style = {{marginTop : 7}}>
 
  <SearchRow
  query={query}
  setQuery={setQuery}
  setPage={setPage}
  load={load}
  activeCategory={activeCategory}
  navigate={navigate}
/>
    
  
  </div>

     {/* CONTINUE LEARNING */}
        {continueReading[0] && (
          <>
           
            <div
              style={styles.continueCard}
              onClick={() =>
                navigate(`/blog/${continueReading[0]._id}`)
              }
            >
             

              <div style={{ flex: 1 }}>
                <p style={styles.mutedSmall}>Continue Learning</p>
                <p style={styles.cardTitle}>
                  {continueReading[0].title}
                </p>
                <div style={styles.progressBar}>
                  <div style={styles.progressFill} />
                </div>
                <button style={styles.primaryBtn}>
                  Continue learning
                </button>
              </div>

               <img
                src={continueReading[0].mediaUrl}
                alt=""
                style={styles.continueImage}
              />
            </div>
          </>
        )}


   {/* Topics */}
   <br/>
   <h6 className="fw-semibold mb-2">Explore by Topic</h6>
   <div className="d-flex gap-2 overflow-auto mb-4">
   {categories.map((cat) => (
       <button
         key={cat}
         onClick={() => handleCategoryChange(cat)}
         className="btn"
         style={{
           borderRadius: 20,
           fontSize: 13,
           fontWeight: 500,
           whiteSpace: "nowrap",
           padding: '6px 16px',
           background: activeTopCategory === cat ? "var(--primary)" : "var(--surface)",
           color: activeTopCategory === cat ? '#fff' : 'var(--on-bg)',
           border: activeTopCategory === cat ? 'none' : '1px solid #e5e7eb',
           transition: 'all 0.18s ease',
           flexShrink: 0,
         }}
       >
         {cat}
       </button>
     ))}
   </div>

   {/* ALL / SAVED TABS */}
   <div className="d-flex gap-2 mb-3">
     <button
       onClick={() => handleTabChange("all")}
       className="btn"
       style={{
         borderRadius: 20,
         fontSize: 13,
         fontWeight: 600,
         padding: '6px 18px',
         background: activeTab === "all" ? "var(--primary)" : "var(--surface)",
         color: activeTab === "all" ? '#fff' : 'var(--on-bg)',
         border: activeTab === "all" ? 'none' : '1px solid #e5e7eb',
       }}
     >
       All Articles
     </button>
     <button
       onClick={() => handleTabChange("saved")}
       className="btn"
       style={{
         borderRadius: 20,
         fontSize: 13,
         fontWeight: 600,
         padding: '6px 18px',
         background: activeTab === "saved" ? "var(--primary)" : "var(--surface)",
         color: activeTab === "saved" ? '#fff' : 'var(--on-bg)',
         border: activeTab === "saved" ? 'none' : '1px solid #e5e7eb',
       }}
     >
       Saved Articles
     </button>
   </div>

   {/* FEATURED FOR YOU */}
<div style={{ marginBottom: 16 }}>
  <div
    onClick={() => navigate(`/blog/${blogs[1]._id}`)}
    style={{
      background: "#ffffff",
      borderRadius: 18,
      overflow: "hidden",
      boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
      cursor: "pointer",
    }}
  >
    {/* Image wrapper */}
    <div style={{ position: "relative" }}>
      <img
        src={blogs[2]?.mediaUrl || placeholder}
        alt={blogs[2]?.title}
        style={{
          width: "100%",
          height: 160,
          objectFit: "cover",
        }}
      />

      {/* Editor's Pick Badge */}
      <span
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          background: "#22c55e",
          color: "#ffffff",
          fontSize: 12,
          fontWeight: 600,
          padding: "4px 10px",
          borderRadius: 999,
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}
      >
        Editor’s Pick
      </span>
    </div>

    {/* Content */}
    <div style={{ padding: 20 }}>
      <h4
        style={{
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1.4,
          margin: "0 0 8px",
        }}
      >
        {blogs[2]?.title}
      </h4>

      <p
        style={{
          fontSize: 13,
          color: "#6b7280",
          marginBottom: 12,
        }}
      >
        {blogs[2]?.author || "Dr. Alice Johnson"} · 5 min read
      </p>

      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "var(--primary)",
        }}
      >
        Read More →
      </span>
    </div>
  </div>
</div>



   {/* RECOMMENDED FOR YOU */}
   {activeTab === "all" && blogs.length > 0 && (
   <>
    <h6 className="fw-semibold mb-1">Recommended for You</h6>
    <p className="text-muted mb-3" style={{ fontSize: 13 }}>
      Based on what you’ve read and your health profile
    </p>

    <div
      className="bg-white shadow-sm"
      style={{ borderRadius: 16, overflow: "hidden" }}
    >
 

      {/* OTHER ARTICLES */}
      {blogs.slice(1).map((b) => (
        <div
          key={b._id}
          className="d-flex align-items-center gap-3 px-3 py-3 border-bottom"
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/blog/${b._id}`)}
        >
          <img
            src={b.mediaUrl || placeholder}
            alt=""
            style={{
              width: 64,
              height: 64,
              borderRadius: 12,
              objectFit: "cover",
            }}
          />

          <div style={{ flex: 1 }}>
            <p className="fw-semibold mb-1">
              {b.title}
            </p>
            <p className="text-muted mb-0" style={{ fontSize: 12 }}>
              {b.tags?.[0] || "Health"} · 4 min read
            </p>
          </div>

          <Bookmark size={18} className="text-muted" />
        </div>
      ))}
      </div>

      <br></br>

<div style={styles.card}>
  <div style={styles.left}>


    <div style={styles.content}>
      <div style={styles.row}>
        <h3 style={styles.title}>Qureo AI Suggests</h3>
     
      </div>

      <p style={styles.text}>
        Based on your interests, you might like to learn about building healthy habits for better sleep.
      </p>
    </div>
  </div>

  <button style={styles.button}>
    Explore 
  </button>
</div>
    
   </>
   )}

   {/* SAVED ARTICLES */}
   {activeTab === "saved" && (
     <div>
       {loadingSaved ? (
         <div className="text-center py-4 text-muted">Loading saved articles...</div>
       ) : savedBlogs.length === 0 ? (
         <div className="text-center py-4 text-muted">No saved articles yet. Tap the bookmark icon on any article to save it here.</div>
       ) : (
         <div
           className="bg-white shadow-sm"
           style={{ borderRadius: 16, overflow: "hidden" }}
         >
           {savedBlogs.map((b) => (
             <div
               key={b._id}
               className="d-flex align-items-center gap-3 px-3 py-3 border-bottom"
               style={{ cursor: "pointer" }}
               onClick={() => navigate(`/blog/${b._id}`)}
             >
               <img
                 src={b.mediaUrl || placeholder}
                 alt=""
                 style={{
                   width: 64,
                   height: 64,
                   borderRadius: 12,
                   objectFit: "cover",
                 }}
               />
               <div style={{ flex: 1 }}>
                 <p className="fw-semibold mb-1">{b.title}</p>
                 <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                   {b.author || "Qureo"} · {new Date(b.createdAt).toLocaleDateString()}
                 </p>
               </div>
               <Bookmark size={18} fill="var(--primary)" color="var(--primary)" />
             </div>
           ))}
         </div>
       )}
     </div>
   )}

 </div>

  );
}

const styles = {
 header: {

    color: "var(--on-bg)",
    position: "relative",
    width: "100%",
    overflow: "visible",
    borderBottomLeftRadius: 25,
    borderBottomRightRadius: 25,
    marginBottom: 20,
    padding: "18px 0 8px",
    fontFamily: "'Poppins', sans-serif",
  },
  headerSubtitle: {
    fontSize: 12,
    marginTop: 4,
    marginLeft: 34,
    textAlign: "left",
  },
  trustBadge: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
    marginLeft: 34,
    marginTop: -7
  },
  trustIcon: {
    fontSize: 14,
    color: "green"
  },
  trustText: {
    fontSize: "0.85em",
    left: "-10px",
    lineHeight: "1",
  },
  headerTop: {
    display: "flex",
    marginTop: 0,
    justifyContent: "space-between",
    alignItems: "center",
  },
   backArrow: {
    cursor: 'pointer',
    flexShrink: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "var(--primary)",
    marginTop: 4,
  },

  headerTitle: {
    fontSize: 22,
    fontWeight: 700,  
    lineHeight: 1.15,
    marginLeft: 12,
    marginBottom: 4,
    marginTop: 0,
  },


  subtitle: {
    fontSize: 14,
    color: "#666",
  },

  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#ffffffff",
    padding: "10px 14px",
    borderRadius: 14,
    marginTop: 14,
  },
  searchInput: {
    border: "none",
    outline: "none",
    width: "100%",
  },
  searchHint: {
    fontSize: 12,
    color: "#888",
    marginTop: 4,
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: 20,
    marginBottom: 10,
  },

  continueCard: {
    display: "flex",
    flexDirection: "column-reverse", // mobile-first layout
    gap: "14px",
    background: "#fff",
    borderRadius: "20px",
    padding: "16px",
    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
    cursor: "pointer",
    width: "100%",
    overflow: "hidden",
  },

  continueImage: {
    width: "100%",
    height: "180px",
    objectFit: "cover",
    borderRadius: "16px",
  },

  mutedSmall: {
    fontSize: "10px",
    color: "#777",
    marginBottom: "6px",
    fontWeight: 500,
  },

  cardTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#111",
    lineHeight: "1.4",
    marginBottom: "14px",
  },

  progressBar: {
    width: "100%",
    height: "8px",
    background: "#ececec",
    borderRadius: "999px",
    overflow: "hidden",
    marginBottom: "16px",
  },

  progressFill: {
    width: "60%",
    height: "100%",
    background: "linear-gradient(90deg, #4F46E5, #7C3AED)",
    borderRadius: "999px",
  },

  primaryBtn: {
    width: "100%",
    padding: "14px",
    border: "none",
    borderRadius: "14px",
    background: "var(--primary)",
    color: "#fff",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
  },

  topicRow: {
    display: "flex",
    gap: 10,
    overflowX: "auto",
  },
  topicPill: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    borderRadius: 999,
    fontSize: 13,
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  recoCard: {
    display: "flex",
    gap: 12,
    background: "#ffffffff",
    padding: 12,
    borderRadius: 14,
    marginBottom: 12,
    alignItems: "center",
  },
  recoImage: {
    width: 64,
    height: 64,
    borderRadius: 10,
    objectFit: "cover",
  },
  simpleCard: {
    background: "#ffffffff",
    padding: 12,
    borderRadius: 14,
    marginBottom: 10,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 800,
  },
  metaText: {
    fontSize: 12,
    color: "#777",
  },
  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    color: "#777",
  },
  mutedSmall: {
    fontSize: 15,
    color: "#888",
  },
  card: {
    width: "100%",
    background: "#F8F6FF",
    border: "1px solid var(--primary)",
    borderRadius: "18px",
    padding: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },

  left: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    minWidth: 0,
  },

  iconWrap: {
    width: "42px",
    height: "42px",
    borderRadius: "12px",
    background: "linear-gradient(135deg,#8B5CF6,#6D28D9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    flexShrink: 0,
  },

  content: {
    minWidth: 0,
    flex: 1,
  },

  row: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "2px",
  },

  title: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "700",
    color: "#1E1B4B",
    whiteSpace: "nowrap",
  },

  badge: {
    background: "var(--primary)",
    color: "#fff",
    fontSize: "9px",
    padding: "2px 6px",
    borderRadius: "999px",
    fontWeight: "600",
  },

  text: {
    margin: 0,
    fontSize: "12px",
    color: "#6B7280",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  button: {
    border: "none",
    background: "inherit",
    color: "var(--primary)",
    padding: "10px 14px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
    flexShrink: 0,
  },
};
